import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, FilterX, Truck } from "lucide-react";
import api from "../lib/api";

export const ProductFilters = ({ category }) => {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const url = category ? `/products/filters?category=${category}` : `/products/filters`;
    api.get(url).then((r) => setFacets(r.data));
  }, [category]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === null || v === "" || v === undefined) next.delete(k);
    else next.set(k, String(v));
    next.delete("page");
    setParams(next, { replace: true });
  };

  const clearAll = () => {
    const next = new URLSearchParams();
    if (params.get("q")) next.set("q", params.get("q"));
    setParams(next, { replace: true });
  };

  if (!facets) return null;

  return (
    <aside
      className="bg-white border border-jow-navy/10 p-5 md:p-6"
      data-testid="product-filters"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="md:hidden w-full flex items-center justify-between mb-3"
      >
        <span className="label-overline text-jow-navy">Filters</span>
        <ChevronDown size={14} className={open ? "rotate-180" : ""} />
      </button>
      <div className={`${open ? "block" : "hidden"} md:block space-y-7`}>
        <div className="flex items-center justify-between">
          <span className="label-overline text-jow-navy/70">Filters</span>
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-jow-pink"
            data-testid="filter-clear"
          >
            <FilterX size={11} /> Clear
          </button>
        </div>

        <Block label="Price (₹)" testid="filter-price">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder={`min ${facets.price.min}`}
              defaultValue={params.get("min_price") || ""}
              onBlur={(e) => setParam("min_price", e.target.value)}
              className="input-jow text-sm border-b"
              data-testid="filter-min-price"
            />
            <input
              type="number"
              placeholder={`max ${facets.price.max}`}
              defaultValue={params.get("max_price") || ""}
              onBlur={(e) => setParam("max_price", e.target.value)}
              className="input-jow text-sm border-b"
              data-testid="filter-max-price"
            />
          </div>
        </Block>

        <Block label="Material" testid="filter-material">
          <RadioList
            options={facets.materials}
            value={params.get("material") || ""}
            onChange={(v) => setParam("material", v)}
            namePrefix="material"
          />
        </Block>

        <Block label="Occasion" testid="filter-occasion">
          <RadioList
            options={facets.occasions}
            value={params.get("occasion") || ""}
            onChange={(v) => setParam("occasion", v)}
            namePrefix="occasion"
          />
        </Block>

        <Block label="Availability" testid="filter-availability">
          <label className="flex items-center gap-3 cursor-pointer text-sm text-jow-navy/80">
            <input
              type="checkbox"
              checked={params.get("in_stock") === "true"}
              onChange={(e) => setParam("in_stock", e.target.checked ? "true" : null)}
              className="accent-jow-pink"
              data-testid="filter-instock"
            />
            In stock only
          </label>
          <label className="flex items-center gap-3 cursor-pointer text-sm text-jow-navy/80 mt-3">
            <input
              type="checkbox"
              checked={!!params.get("available_on_rikshaw")}
              onChange={(e) => {
                // pick the first rikshaw id if user checks; backend expects a specific id
                // simpler: when checked, set to "any" handled client-side via post-filter — but our API needs an id.
                // Use a sentinel to mean "any", then we filter results client-side.
                setParam("available_on_rikshaw_any", e.target.checked ? "1" : null);
              }}
              className="accent-jow-pink"
              data-testid="filter-on-rikshaw"
            />
            <Truck size={13} className="text-jow-gold" /> On a rikshaw now
          </label>
        </Block>
      </div>
    </aside>
  );
};

function Block({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="label-overline text-jow-navy mb-3">{label}</p>
      {children}
    </div>
  );
}

function RadioList({ options, value, onChange, namePrefix }) {
  return (
    <ul className="space-y-2">
      <li>
        <label className="flex items-center gap-3 cursor-pointer text-sm text-jow-navy/80">
          <input
            type="radio"
            name={namePrefix}
            checked={!value}
            onChange={() => onChange(null)}
            className="accent-jow-pink"
            data-testid={`${namePrefix}-any`}
          />
          Any
        </label>
      </li>
      {options.map((opt) => (
        <li key={opt}>
          <label className="flex items-center gap-3 cursor-pointer text-sm text-jow-navy/80">
            <input
              type="radio"
              name={namePrefix}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="accent-jow-pink"
              data-testid={`${namePrefix}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
            />
            {opt}
          </label>
        </li>
      ))}
    </ul>
  );
}
